import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { LoginPage } from './components/auth/LoginPage'
import { SignupPage } from './components/auth/SignupPage'
import { ResetPasswordPage } from './components/auth/ResetPasswordPage'
import { UpdatePasswordPage } from './components/auth/UpdatePasswordPage'
import { AppShell } from './components/layout/AppShell'
import { ExecutiveCockpit } from './components/dashboard/ExecutiveCockpit'
import { RevenueOps } from './pages/RevenueOps'
import { MarginControl } from './pages/MarginControl'
import { Billing } from './pages/Billing'
import { RecurringRevenue } from './pages/RecurringRevenue'
import { Upload } from './pages/Upload'
import { Sources } from './pages/Sources'
import { Orders } from './pages/Orders'
import { AiChat } from './pages/AiChat'
import { Export } from './pages/Export'
import { ErrorBoundary } from './components/ui/ErrorBoundary'
import { Onboarding } from './pages/Onboarding'
import { Settings } from './pages/Settings'
import { PlanPage } from './pages/PlanPage'
import { UpgradePage } from './pages/UpgradePage'
import { Customers } from './pages/Customers'
import { CustomerDetail } from './pages/CustomerDetail'
import { UploadedFiles } from './pages/UploadedFiles'
import { FolderWatches } from './pages/FolderWatches'
import { BusinessUnitsPage } from './pages/BusinessUnitsPage'
import { CostEntriesPage } from './pages/CostEntriesPage'
import { BusinessReview } from './pages/BusinessReview'
import { SharedDashboard } from './pages/SharedDashboard'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { AdminAuthProvider } from './admin/context/AdminAuthContext'
import { AdminLayout } from './admin/layout/AdminLayout'
import { AdminLogin } from './admin/pages/AdminLogin'
import { AdminHome } from './admin/pages/AdminHome'
import { AdminTenants } from './admin/pages/AdminTenants'
import { AdminTenantDetail } from './admin/pages/AdminTenantDetail'
import { AdminAuditLog } from './admin/pages/AdminAuditLog'
import { AdminMetrics } from './admin/pages/AdminMetrics'
import { AdminPlanLimits } from './admin/pages/AdminPlanLimits'
import { AdminMappingTemplates } from './admin/pages/AdminMappingTemplates'
import { ImpersonationProvider } from './contexts/ImpersonationContext'
import { MemberProvider } from './contexts/MemberContext'
import { CollaboratorsPage } from './pages/CollaboratorsPage'
import { AcceptInvitePage } from './pages/AcceptInvitePage'
import { SectionGuard } from './components/auth/SectionGuard'
import './styles/globals.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <ImpersonationProvider>
          <ThemeProvider>
          <AdminAuthProvider>
          <MemberProvider>
          <Routes>
            {/* Rotte pubbliche */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/update-password" element={<UpdatePasswordPage />} />

            {/* Admin area — JWT separato, nessun tenant context */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminHome />} />
              <Route path="tenants" element={<AdminTenants />} />
              <Route path="tenants/:tenantId" element={<AdminTenantDetail />} />
              <Route path="audit" element={<AdminAuditLog />} />
              <Route path="metriche" element={<AdminMetrics />} />
              <Route path="piani" element={<AdminPlanLimits />} />
              <Route path="mapping-templates" element={<AdminMappingTemplates />} />
            </Route>

            {/* Dashboard condivisa — pubblica, senza auth */}
            <Route path="/shared/:token" element={<SharedDashboard />} />

            {/* Onboarding — protetta ma senza AppShell */}
            <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />

            {/* Accettazione invito collaboratore */}
            <Route path="/invito/:token" element={<ProtectedRoute><AcceptInvitePage /></ProtectedRoute>} />

            {/* Rotte protette — AppShell gestisce sidebar + topbar */}
            <Route element={<AppShell />}>
              <Route index         element={<SectionGuard section="executive"><ErrorBoundary section="Executive Cockpit"><ExecutiveCockpit /></ErrorBoundary></SectionGuard>} />
              <Route path="revenue" element={<SectionGuard section="revenue_ops"><ErrorBoundary section="Revenue Ops"><RevenueOps /></ErrorBoundary></SectionGuard>} />
              <Route path="margin"  element={<SectionGuard section="margin"><ErrorBoundary section="Margin Control"><MarginControl /></ErrorBoundary></SectionGuard>} />
              <Route path="billing" element={<SectionGuard section="billing"><ErrorBoundary section="Billing & AR"><Billing /></ErrorBoundary></SectionGuard>} />
              <Route path="recurring-revenue" element={<SectionGuard section="recurring"><ErrorBoundary section="Recurring Revenue"><RecurringRevenue /></ErrorBoundary></SectionGuard>} />
              <Route path="upload"  element={<ErrorBoundary section="Carica file"><Upload /></ErrorBoundary>} />
              <Route path="sources" element={<ErrorBoundary section="Sorgenti"><Sources /></ErrorBoundary>} />
              <Route path="orders"  element={<ErrorBoundary section="Commesse"><Orders /></ErrorBoundary>} />
              <Route path="ai"           element={<ErrorBoundary section="AI Chat"><AiChat /></ErrorBoundary>} />
              <Route path="export"       element={<ErrorBoundary section="Esporta"><Export /></ErrorBoundary>} />
              <Route path="impostazioni" element={<ErrorBoundary section="Impostazioni"><Settings /></ErrorBoundary>} />
              <Route path="impostazioni/piano" element={<ErrorBoundary section="Piano"><PlanPage /></ErrorBoundary>} />
              <Route path="impostazioni/business-units" element={<ErrorBoundary section="Business Unit"><BusinessUnitsPage /></ErrorBoundary>} />
              <Route path="impostazioni/collaboratori" element={<ErrorBoundary section="Collaboratori"><CollaboratorsPage /></ErrorBoundary>} />
              <Route path="costi" element={<SectionGuard section="cost_entries"><ErrorBoundary section="Costi"><CostEntriesPage /></ErrorBoundary></SectionGuard>} />
              <Route path="upgrade" element={<ErrorBoundary section="Upgrade"><UpgradePage /></ErrorBoundary>} />
              <Route path="clienti"      element={<ErrorBoundary section="Clienti"><Customers /></ErrorBoundary>} />
              <Route path="clienti/:customerName" element={<ErrorBoundary section="Scheda cliente"><CustomerDetail /></ErrorBoundary>} />
              <Route path="file-caricati"  element={<ErrorBoundary section="File caricati"><UploadedFiles /></ErrorBoundary>} />
              <Route path="cartelle-cloud" element={<ErrorBoundary section="Cartelle cloud"><FolderWatches /></ErrorBoundary>} />
              <Route path="business-review" element={<ErrorBoundary section="Business Review"><BusinessReview /></ErrorBoundary>} />
            </Route>
          </Routes>
          </MemberProvider>
          </AdminAuthProvider>
          </ThemeProvider>
          </ImpersonationProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
